import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { publicApi } from './api.ts';
import type { PortalSettings } from './types.ts';

type PortalSettingsContextValue = {
  settings: Partial<PortalSettings>;
  loading: boolean;
  reload: () => Promise<void>;
};

const PortalSettingsContext = createContext<PortalSettingsContextValue>({
  settings: {},
  loading: true,
  reload: async () => {},
});

export function PortalSettingsProvider({ children }: { children: ReactNode }): ReactNode {
  const [settings, setSettings] = useState<Partial<PortalSettings>>({});
  const [loading, setLoading] = useState(true);

  const load = useMemo(
    () => async () => {
      try {
        const res = await publicApi.get<{ settings: Partial<PortalSettings> }>('/api/settings');
        setSettings(res.settings ?? {});
      } catch {
        setSettings({});
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const value = useMemo<PortalSettingsContextValue>(
    () => ({ settings, loading, reload: load }),
    [settings, loading, load],
  );

  return <PortalSettingsContext.Provider value={value}>{children}</PortalSettingsContext.Provider>;
}

export function usePortalSettings(): PortalSettingsContextValue {
  return useContext(PortalSettingsContext);
}
